import { useMemo, useState } from 'react';

import { Box, Divider, Flex, Grid } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  openAccessControlModal,
  setEditMode,
  setTemplateData,
} from 'screens/admin/Objectives/GoalPreferences/slice/accessControlSlice';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { GoalSettings, Plus } from 'assets/assetsV1/iconComponents';
import TemplateImage1 from 'assets/images/goal-template-1.png';
import TemplateImage2 from 'assets/images/goal-template-2.png';
import TemplateImage3 from 'assets/images/goal-template-3.png';
import TemplateImage4 from 'assets/images/goal-template-4.png';

import { Typography14 } from 'uiToolkit/Typography';
import { Button } from 'uiToolkitV1/Button';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import TemplateCard from './components/TemplateCard';
import { useAllTemplatesQuery } from './hooks/dataHooks';
import { initialStateGoalAccessMatrix } from './slice/initialStateGoalAccessMatrix';

const GoalSettingsIcon = (props) => <GoalSettings {...props} fill="transparent" />;

const BASE_TEMPLATES = [
  {
    heading: 'OKR',
    description: 'Achieve 10x growth and impact by setting, tracking clear and challenging objectives',
    lastEditedByImage: '',
    lastEditedByName: 'Neeraj',
    isSelected: true,
    imageUrl: TemplateImage1,
  },
  {
    heading: 'SMART Goals',
    description: 'Enhance your productivity and motivation by setting and achieving SMART goals',
    lastEditedByImage: '',
    lastEditedByName: 'Neeraj',
    isSelected: false,
    imageUrl: TemplateImage2,
  },
  {
    heading: 'KPIs',
    description: 'Track your team’s progress & performance with clear and actionable metrics',
    lastEditedByImage: '',
    lastEditedByName: 'Neeraj',
    isSelected: false,
    imageUrl: TemplateImage3,
  },
  {
    heading: 'Custom OKR Template 1',
    description: 'Parents will be mandatory while creating OKRs/ Goals',
    lastEditedByImage: '',
    lastEditedByName: 'Neeraj',
    isSelected: false,
    imageUrl: TemplateImage4,
  },
];

const Templates = () => {
  const dispatch = useDispatch();

  const currentEmployeeData = useSelector(selectLoggedInEmployeeData);

  const { data, isLoading } = useAllTemplatesQuery();

  const [showAllTemplates, setShowAllTemplates] = useState(false);

  const openModal = () => {
    const newTemplateData = {
      ...initialStateGoalAccessMatrix,
      orgId: currentEmployeeData?.organization?.id,
    };

    dispatch(setTemplateData(newTemplateData));
    dispatch(setEditMode(true));
    dispatch(openAccessControlModal());
  };

  const templatesToShow = useMemo(() => (showAllTemplates ? data : data?.slice(0, 4)) ?? [], [data, showAllTemplates]);

  if (isLoading) {
    return (
      <Box mb="2rem" bgColor="white" padding="2rem" borderRadius={8}>
        <SkeletonLoader />
      </Box>
    );
  }

  return (
    <Box mb="2rem" bgColor="white" padding="2rem" borderRadius={8}>
      <Flex justifyContent="space-between" alignItems="center" mb="1rem">
        <ListItemComp
          withoutBackground
          LeftComponent={GoalSettingsIcon}
          title="Goal Setting Methodology"
          subTitle="Select a goal setting method: pre-existing or custom with custom names, visibility, and access control."
          p={0}
          px={1}
        />
        <Button
          variant="rowButtonOutlined"
          StartIcon={Plus}
          startIconProps={{ fill: 'transparent' }}
          onClick={openModal}
        >
          Add New
        </Button>
      </Flex>
      <Divider />
      <Grid mt="1rem" templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
        {templatesToShow?.map((templateData, index) => (
          <TemplateCard data={templateData} key={templateData.uuid} index={index} />
        ))}
      </Grid>

      {!data?.length && (
        <Box mt="1rem" mb="2rem">
          <Typography14 style={{ fontWeight: '600', textAlign: 'center' }}>No Templates found</Typography14>
        </Box>
      )}

      {data?.length > 4 && !showAllTemplates && (
        <Flex mt="24px" justifyContent="center">
          <Button
            onClick={() => {
              setShowAllTemplates(true);
            }}
            variant="tertiary"
          >
            View More Templates
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export default Templates;
